import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import LoyaltyStampsCard from "@/components/LoyaltyStampsCard";
import MutedBusinessesCard from "@/components/MutedBusinessesCard";
import InviteCustomerCard from "@/components/InviteCustomerCard";
import CustomerPromoCategoriesCard from "@/components/CustomerPromoCategoriesCard";
import PlaylistsCard from "@/components/PlaylistsCard";
import CustomerAvatarCard from "@/components/CustomerAvatarCard";
import BirthdayPreferenceCard from "@/components/BirthdayPreferenceCard";
import PushRegistrationCard from "@/components/PushRegistrationCard";
import MyFavoritesCard from "@/components/MyFavoritesCard";
import FriendsCard from "@/components/FriendsCard";
import SuggestionForm from "@/components/SuggestionForm";
import { UserPlus, Gift } from "lucide-react";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [written, setWritten] = useState([]);
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    api.get("/me/reviews/written").then(({ data }) => setWritten(data));
    api.get("/me/referrals").then(({ data }) => setReferrals(data?.items || [])).catch((e) => console.warn("referrals load failed:", e));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      <div className="overline text-gray-500">My profile</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">{user?.name}</h1>
      <p className="text-gray-600 mt-2">{user?.address}</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-sm border border-border p-6">
          <div className="overline text-gray-500">Your customer rating</div>
          <div className="mt-3 flex items-center gap-2">
            <StarRating value={user?.customer_rating_avg || 0} size={22} />
            <span className="font-display text-2xl">{(user?.customer_rating_avg || 0).toFixed(1)}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">{user?.customer_rating_count || 0} business reviews</p>
        </Card>
        <Card className="rounded-sm border border-border p-6">
          <div className="overline text-gray-500">Reviews written</div>
          <div className="mt-3 font-display text-3xl">{written.length}</div>
        </Card>
        <Card className="rounded-sm border border-border p-6">
          <div className="overline text-gray-500">Discover</div>
          <p className="text-sm text-gray-600 mt-2">Browse businesses and share your experiences.</p>
          <Link to="/directory" className="mt-3 inline-block"><Button variant="outline" size="sm" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="customer-browse-button">Open directory</Button></Link>
        </Card>
      </div>

      <Badge variant="outline" className="rounded-sm mt-10 mb-3">Your reviews</Badge>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {written.length === 0 && <p className="text-gray-500 text-sm">You haven't reviewed any business yet.</p>}
        {written.map((r) => (
          <Card key={r.id} className="rounded-sm border border-border p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>
              <StarRating value={r.rating} />
            </div>
            <p className="mt-2 text-sm">{r.comment}</p>
          </Card>
        ))}
      </div>

      <div className="mt-10">
        <CustomerAvatarCard />
      </div>

      <div className="mt-10">
        <BirthdayPreferenceCard />
      </div>

      <div className="mt-10">
        <PushRegistrationCard />
      </div>

      <div className="mt-10">
        <MyFavoritesCard />
      </div>

      <div className="mt-10">
        <FriendsCard />
      </div>

      <div className="mt-10">
        <LoyaltyStampsCard />
      </div>

      <div className="mt-10">
        <CustomerPromoCategoriesCard />
      </div>

      <div className="mt-10">
        <PlaylistsCard />
      </div>

      <div className="mt-10">
        <MutedBusinessesCard />
      </div>

      <div className="mt-10">
        <SuggestionForm />
      </div>

      <div className="mt-10">
        <InviteCustomerCard />
      </div>

      <Badge variant="outline" className="rounded-sm mt-10 mb-3 inline-flex items-center gap-1.5"><UserPlus size={12} /> My referrals</Badge>
      <Card className="rounded-md border border-border p-6" data-testid="customer-referrals-card">
        <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">
          Refer friends to businesses you love and earn rewards. Open any business profile you've reviewed, been reviewed by, or been added by, and click <strong>Refer a friend</strong> — your friend gets a welcome offer, and you get a thank-you reward when they claim it.
        </p>
        <Link to="/directory" className="inline-block mt-3"><Button variant="outline" size="sm" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="customer-find-biz-to-refer">Find a business to refer</Button></Link>

        {referrals.length === 0 ? (
          <p className="mt-6 text-sm text-gray-500" data-testid="customer-referrals-empty">You haven't sent any referrals yet.</p>
        ) : (
          <ul className="mt-6 space-y-2" data-testid="customer-referrals-list">
            {referrals.map((r) => (
              <li key={r.id} className="flex items-center justify-between border border-border rounded-sm px-4 py-3" data-testid={`customer-referral-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {r.referee_name} <span className="font-normal text-gray-500">→ {r.business_name || "a business"}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Sent {new Date(r.created_at).toLocaleDateString()} · Code <span className="font-mono">{r.code}</span>
                  </div>
                </div>
                <Badge variant="outline" className={`rounded-sm text-[10px] font-mono shrink-0 ml-3 ${
                  r.status === "redeemed" ? "border-emerald-400 text-emerald-700" :
                  r.status === "claimed" ? "border-[#B31E1E] text-[#B31E1E]" : "border-gray-300 text-gray-600"
                }`}>
                  {r.status === "redeemed" && <Gift size={10} className="inline mr-1" />}
                  {r.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

